import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideCheckNoChangesConfig,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideHighcharts } from 'highcharts-angular';
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import {
  provideRouter,
  withRouterConfig,
  InMemoryScrollingOptions,
  InMemoryScrollingFeature,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { provideClientHydration, withNoIncrementalHydration } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsDispatchPluginModule } from '@ngxs-labs/dispatch-decorator';
import { ngxsConfig } from './ngxs.config';
import { stateRollUp } from './store/index.state';
import { NgxsModule } from '@ngxs/store';
import { UrlHelper } from './store/helpers/url-helper';
import { NgSelectModule } from '@ng-select/ng-select';
import { DashboardSort } from './store/helpers/dashboard-sort';
import { DeviceDetectorService } from 'ngx-device-detector';
import { GoogleDataLayerService } from './services/google-data-layer.service';
import { CookieService } from 'ngx-cookie-service';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { provideUserIdleConfig } from 'angular-user-idle';
import {
  GatingService,
} from './services/index';
import { AppInitializerService } from './app-initializer/app-initializer.service';
import { DashboardApiService } from './store/services/dashboard-api.service';
import {
  NgxMaskDirective,
  NgxMaskPipe
} from 'ngx-mask';
import {
  BearerAuthInterceptor,
  NoCacheInterceptor,
  NewRelicLoggingInterceptor,
  UnauthorizedResponseInterceptor,
} from './interceptors/index';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { CustomPreloading } from './custom-preloading';
import { APP_BASE_HREF } from '@angular/common';
import { routes } from './app.routes'
import { UniversalDeviceDetectorService } from './services/universal-device-detector.service';
import { ChunkLoadErrorHandler } from './services/chunk-load-error-handler.service';
import { environment } from '../environments/environment';

const CUSTOM_PRELOADING_PROVIDER = [CustomPreloading];

export function loadApiEnvironment(appInitializerService: AppInitializerService) {
  return () => appInitializerService.loadApiEnvironment();
}

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
};

const inMemoryScrollingFeature: InMemoryScrollingFeature =
  withInMemoryScrolling(scrollConfig);

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      NgxsModule.forRoot([...stateRollUp], ngxsConfig),
      // Only load Redux DevTools in development mode to reduce SSR overhead
      ...(ngxsConfig.developmentMode ? [NgxsReduxDevtoolsPluginModule.forRoot()] : []),
      NgxsDispatchPluginModule.forRoot(),
    ),
    NgxMaskDirective,
    NgxMaskPipe,
    provideRouter(
      routes,
      withPreloading(CustomPreloading),
      // withDebugTracing(),
      inMemoryScrollingFeature,
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
    ),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideUserIdleConfig({ idle: 1800, timeout: 120, ping: 1000 }), // idle: 30 min, timeout: 2 min, ping: 16.7 min
    provideAngularSvgIcon(),
    provideHighcharts({
      instance: () =>
        import('highcharts')
          .then((m) => m.default || (m as any))
          .then((hc) =>
            import('./helpers/highcharts.helper').then((helper) =>
              helper.HighchartsHelper.initialize(hc),
            ),
          ),
    }),
    provideClientHydration(withNoIncrementalHydration()),
    {
      provide: APP_BASE_HREF,
      useValue: '/'
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BearerAuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NoCacheInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NewRelicLoggingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UnauthorizedResponseInterceptor,
      multi: true,
    },
    provideAppInitializer(() => {
      const initializerFn = (loadApiEnvironment)(inject(AppInitializerService));
      return initializerFn();
    }),
    provideAnimationsAsync(),
    provideZonelessChangeDetection(),
    // Local dev only: periodically re-checks every view and throws NG0100 for any
    // binding that changed without notifying Angular (plain field mutated from a
    // subscribe/timer/third-party callback). These are the spots that silently
    // stop updating without zone.js.
    ...(isDevMode() && environment.environmentName === 'local'
      ? [provideCheckNoChangesConfig({ exhaustive: true, interval: 1000 })]
      : []),
    { provide: DeviceDetectorService, useClass: UniversalDeviceDetectorService },
    DialogService,
    providePrimeNG({
      license: environment.primeNgLicense,
      // Lift PrimeNG overlays above Angular CDK overlay (z-index 1000), which
      // is used by MatDialog / dialog-right. Without this, tooltips, selects,
      // multiselects and autocompletes rendered inside a dialog can paint
      // behind the dialog surface.
      zIndex: {
        modal: 2000,
        overlay: 1500,
        menu: 1500,
        tooltip: 1500,
      },
    }),
    // Angular CDK 21 renders overlays into the browser's top layer via the
    // native `<dialog popover>` API by default. Top-layer elements paint above
    // everything in the normal layer regardless of z-index — which means body-
    // level PrimeNG overlays (tooltip, select, multiselect, autocomplete) can
    // never appear above a MatDialog while popover mode is on. Opt out so the
    // z-index config above actually wins.
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    { provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
    UrlHelper,
    DashboardApiService,
    CookieService,
    SsrCookieService,
    GoogleDataLayerService,
    GatingService,
    DashboardSort,
    CUSTOM_PRELOADING_PROVIDER,
    NgSelectModule
  ]
};
